import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ICD_ORDER_TYPE_LIST } from '@core/constants/ICD';
import { ICDCode, ICDOrderType } from '@shared/model';
import { SelectItem } from 'primeng/api';
import { ListboxClickEvent } from 'primeng/listbox';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'diagnosis-view-lists',
    templateUrl: './diagnosis-view-lists.component.html',
    styleUrls: ['./diagnosis-view-lists.component.scss'],
    standalone: false
})
export class DiagnosisViewListsComponent {
  @Input() hasEditPermission: boolean;
  @Input() isInterruption: boolean;

  @Input() set icds(value: ICDCode[]) {
    this.icdsData = value;
    this.availableIcds = this.getAvailableIcds(value);
  };

  get icds(): ICDCode[] {
    return this.icdsData;
  }

  @Input() set selectedIcds(value: ICDCode[]) {
    if (value.length > 0) {
      this.newSelectedIcds = value.map(icd => {
        this.isUnknownIcd = this.checkIcdUnknown(icd);

        return this.createICDSelectItem(icd);
      });
    } else {
      this.isUnknownIcd = false;
    }

    this.canAddIcds = this.checkCanAddIcds();

    this.availableIcds = this.getAvailableIcds(this.icds);
  };

  @Output() selectedIcdsChange = new EventEmitter<ICDCode[]>();

  availableIcds: SelectItem<ICDCode>[] = [];
  newSelectedIcds: SelectItem<ICDCode>[] = [];
  icdsData: ICDCode[] = [];
  hasEditablePermission = false;

  canAddIcds = true;

  private isUnknownIcd = false;
  private readonly unknownIcdCode = 'Unknown';
  private readonly IcdsCodeMaxCount = 4;

  readonly orderTypeList: ICDOrderType[] = [...ICD_ORDER_TYPE_LIST];

  checkCanAddIcds(): boolean {
    return !this.isUnknownIcd && this.newSelectedIcds.length < this.IcdsCodeMaxCount;
  }

  getAvailableIcds(value: ICDCode[]): SelectItem<ICDCode>[] {
    return this.filterSelectedIcd(value).map(icd => this.createICDSelectItem(icd));
  }

  moveSelectedIcd(icd: ICDCode, moveUp: boolean): void {
    const selectedIndex = this.newSelectedIcds.findIndex((e) => e.value === icd);
    const temp = this.newSelectedIcds[selectedIndex];
    const neighborIndex = moveUp ? selectedIndex - 1 : selectedIndex + 1

    this.newSelectedIcds[selectedIndex] = this.newSelectedIcds[neighborIndex];
    this.newSelectedIcds[neighborIndex] = temp;

    this.emitIcds();
  }

  selectIcd(select: ListboxClickEvent): void {
    const selectedOption = select.option as SelectItem<ICDCode>;
    const selectedValue = selectedOption.value;

    this.isUnknownIcd = this.checkIcdUnknown(selectedValue);
    this.newSelectedIcds = this.isUnknownIcd
      ? [selectedOption]
      : [...this.newSelectedIcds, selectedOption];

    this.availableIcds.splice(
      this.availableIcds.findIndex((e) => e.value === selectedValue),
      1
    );

    this.canAddIcds = this.checkCanAddIcds();
    this.emitIcds();
  }

  removeIcd(event: SelectItem<ICDCode>): void {
    const selectedIcd = this.icds.find(
      (el) => el.icdCodeID === event.value.icdCodeID);

    if (selectedIcd) {
      const haveUnknown = this.checkIcdUnknown(event.value);

      this.availableIcds = [...this.availableIcds, event].sort((a, b) =>
        a.label.localeCompare(b.label)
      );

      if (haveUnknown) {
        this.isUnknownIcd = false;
        this.canAddIcds = this.checkCanAddIcds();
      }
    }
    this.newSelectedIcds.splice(
      this.newSelectedIcds.findIndex((e) => e.value === event.value),
      1
    );

    this.emitIcds();
  }

  filterSelectedIcd(icdCodes: ICDCode[]) {
    if(icdCodes.length) {
      return icdCodes.filter(({ icdCodeID }) =>
        !this.newSelectedIcds.find(
          ({ value: selectedICD }) =>
            icdCodeID === selectedICD.icdCodeID
        )
      )
      .sort((a, b) => a.code.localeCompare(b.code));
    }

    return [];
  }

  private createICDSelectItem(icd: ICDCode): SelectItem<ICDCode> {
    return {
      value: {...icd},
      label: `${icd.code} - ${icd.description}`,
    };
  }

  private checkIcdUnknown(icd: ICDCode): boolean {
    return icd.code === this.unknownIcdCode;
  }

  private emitIcds(): void {
    this.selectedIcdsChange.emit(this.newSelectedIcds.map(icdItem => icdItem.value));
  }
}
